import * as T from 'three';

/** Photographic PBR surfaces for man-made props. ARM maps straight onto three's AO/roughness/metalness channels. */
export type Surface = 'wood' | 'roof' | 'tiles' | 'metal' | 'brick' | 'plaster' | 'concrete' | 'paint' | 'fabric' | 'glass';
const SETS: Partial<Record<Surface, { id: string; tile: number; metal?: number }>> = {
  wood: { id: 'distressed_painted_planks', tile: 2.4 }, roof: { id: 'grey_roof_tiles', tile: 2.6 }, tiles: { id: 'clay_roof_tiles', tile: 2.2 },
  metal: { id: 'corrugated_iron', tile: 2.2, metal: .6 }, brick: { id: 'brick_wall_02', tile: 2.4 }, plaster: { id: 'grey_plaster', tile: 3 },
  concrete: { id: 'concrete_wall_001', tile: 3 },
};
const BASE = `${import.meta.env.BASE_URL}assets/env/buildings/`;
const loader = new T.TextureLoader();
const textures = new Map<string, { map: T.Texture; normal: T.Texture; arm: T.Texture }>();
function set(id: string, anisotropy: number) {
  if (!textures.has(id)) {
    const load = (name: string, color: boolean) => {
      const t = loader.load(`${BASE}${id}/${name}.webp`, undefined, undefined, () => { /* offline: flat colour remains */ });
      t.wrapS = t.wrapT = T.RepeatWrapping; t.anisotropy = anisotropy; t.colorSpace = color ? T.SRGBColorSpace : T.NoColorSpace; return t;
    };
    textures.set(id, { map: load('albedo', true), normal: load('normal', false), arm: load('arm', false) });
  }
  return textures.get(id)!;
}

const cache = new Map<string, T.MeshStandardMaterial>();
/** A tinted photographic material. Geometry UVs must be in metres (see metricUVs). */
export function surface(kind: Surface, color: string, anisotropy = 8): T.MeshStandardMaterial {
  const key = `${kind}|${color}`;
  if (cache.has(key)) return cache.get(key)!;
  const spec = SETS[kind];
  let material: T.MeshStandardMaterial;
  if (kind === 'glass') material = new T.MeshPhysicalMaterial({ color, roughness: .05, metalness: 0, clearcoat: 1, envMapIntensity: 1.4 });
  else if (kind === 'paint') material = new T.MeshPhysicalMaterial({ color, roughness: .38, metalness: 0, clearcoat: .6, clearcoatRoughness: .25 });
  else if (kind === 'fabric') material = new T.MeshPhysicalMaterial({ color, roughness: .9, metalness: 0, sheen: .6, sheenRoughness: .7, sheenColor: new T.Color(color).lerp(new T.Color('#ffffff'), .4), side: T.DoubleSide });
  else {
    const t = set(spec!.id, anisotropy), repeat = 1 / spec!.tile;
    const clone = (tex: T.Texture) => { const c = tex.clone(); c.repeat.set(repeat, repeat); c.needsUpdate = true; return c; };
    material = new T.MeshStandardMaterial({ color, map: clone(t.map), normalMap: clone(t.normal), roughnessMap: clone(t.arm), aoMap: clone(t.arm), metalnessMap: clone(t.arm), metalness: spec!.metal ?? 1, roughness: 1, aoMapIntensity: .8 });
    material.normalScale.set(1, 1);
  }
  cache.set(key, material);
  return material;
}
export function clearSurfaces() { for (const m of cache.values()) m.dispose(); cache.clear(); }

/** Rewrites UVs so one unit equals one metre on every face of a box or around a cylinder. */
export function metricUVs(geometry: T.BufferGeometry) {
  const params = (geometry as T.BufferGeometry & { parameters?: Record<string, number> }).parameters ?? {};
  const uv = geometry.attributes.uv as T.BufferAttribute;
  if (geometry instanceof T.BoxGeometry) {
    const { width: w, height: h, depth: d } = params;
    const dims: [number, number][] = [[d, h], [d, h], [w, d], [w, d], [w, h], [w, h]];
    const perFace = uv.count / 6;
    for (let f = 0; f < 6; f++) for (let i = 0; i < perFace; i++) { const k = f * perFace + i; uv.setXY(k, uv.getX(k) * dims[f][0], uv.getY(k) * dims[f][1]); }
  } else if (geometry instanceof T.CylinderGeometry || geometry instanceof T.ConeGeometry) {
    const r = Math.max(params.radiusTop ?? params.radius ?? 1, params.radiusBottom ?? params.radius ?? 1), h = params.height ?? 1;
    for (let k = 0; k < uv.count; k++) uv.setXY(k, uv.getX(k) * Math.PI * 2 * r, uv.getY(k) * h);
  }
  uv.needsUpdate = true;
  return geometry;
}
